// Charges the Zepto engraving fee as a REAL nested cart line — a `gravering-60/80/100`
// product added with `parent_line_key` under the engraved line — instead of Zepto's cart
// transform, whose day-rate FX conversion drifted off the labelled amount on non-DKK
// markets (label 90 SEK, charge 91). A real line prices from the Markets price lists and
// lets a discount code target the `Gravering` collection alone (the newsletter
// "50% off engraving" offer), which is why this design exists.
//
// DIVISION OF LABOUR WITH ZEPTO. byRavn zeroes a product's engraving price in the Zepto
// app; at price 0 Zepto never writes `_pplr_cart_variant`, so its transform, its post-add
// positional /cart/change rewrite and its fee all disengage for that line. This module
// charges ONLY those disarmed lines. A line still carrying `_pplr_cart_variant` is
// Zepto-charged: never add a child there (a combined add on such a parent returns 200 and
// silently drops the child — status is not a sufficient success check), and remove any
// child that ends up under one, or the fee would be charged twice.
//
// HOW A FEE IS RECOGNISED. Once the app-side price is 0, the only trace of the fee is the
// "(+60 kr.)" tier suffix byRavn types into the field label. It survives in the submitted
// property NAME (text/upload fields) or in the chosen option VALUE (dropdowns). Each
// filled property carrying a known tier suffix is one priced field; the parent's one
// child line per tier has quantity = priced fields × parent quantity. Checkout-mirror
// rows (listed in the `_byRavn_checkout_mirrors` manifest) are duplicates of fields
// already counted and are excluded — as is every pplr/byRavn/zepto/font bookkeeping
// property.
//
// WHY A RECONCILER AND NOT AN ADD-HOOK. Zepto owns the PDP add (jQuery XHR, single-item
// form POST whose response its success handler parses — injecting a second item into
// that request would flip the response to the `{items:[...]}` shape and break the
// handler, whose mismatch path CLEARS THE CART). So the child is attached in a follow-up
// request, and correctness comes from convergence: every cart mutation (Zepto add/change
// via the byRavn:zepto-* events, Dawn quantity changes via cartUpdate, plus a pass on
// cart-page load) triggers a reconcile that computes the want-state from the LIVE cart
// and asserts it landed, retrying once. Nothing here trusts a 200, a DOM-cached key or a
// previous pass.
//
// THE NONCE. Two same-variant parents differing only in engraving text get different
// parent keys but would get IDENTICAL child keys (the child key hashes the parent
// variant, not the parent line) — /cart/change on that shared key hits BOTH children.
// Every child therefore carries an opaque `_byRavn_gravering_ref` nonce so its key is
// unique. Never derive the nonce from the parent's key (parents re-key constantly);
// children are resolved through `parent_relationship.parent_key`, which Shopify migrates
// itself when the parent re-keys.
//
// THE GIFT BAG RIDES THE SAME RECONCILER. Since the gavepose migration (2026-08-25) the
// gift bag on an UNARMED line is a nested child too — byRavn-gavepose.js only toggles a
// bag child's EXISTENCE (that is user intent, never derivable from the cart), while
// this module owns every size invariant: a bag child's quantity is its parent's
// quantity, kept by the same shrink-only pre-pass and cartUpdate reconcile as the fee
// children. Bag children carry their own `_byRavn_gavepose_ref` nonce for the identical
// key-collision reason.
//
// ARMED LINES CANNOT NEST — the flat bag survives for exactly them. Shopify rejects a
// `parent_line_key` add under any line a cart transform manages (422 "Sampak-produkter
// og tilføjelser kan ikke blandes", verified 2026-08-25 on this store), and every armed
// line is Zepto-transform-managed. So an armed line's wrap keeps the flat design — the
// visible `Gavepose: Ja` property (stamped by the toggle) plus the ONE flat
// propertyless `indpakning-i-gavepose` line. That design is permanent: BOX is never
// zeroed (its whole price is Zepto's per-character fee), so its lines always arm. The
// flat bag is fully DERIVED state here: its quantity is the sum of flagged line
// quantities, and the reconciler creates, resizes and removes it to match (that
// authority was byRavn-gavepose.js's syncBagLine before the migration; it moved here so
// one module owns every size invariant). An unarmed line never gets a flag (the toggle
// nests there), but one that carries a flag anyway counts like an armed one: that
// keeps the charge equal to the fold cart-gavepose-totals.liquid displays — except for
// a flagged line whose product lost the `gavepose` tag, which the fold excludes while
// this module still counts it (/cart.js line items carry no tags).
(function () {
  const CONFIG = window.byRavnGravering;
  // The fee side is inert until all three tier products exist and are purchasable —
  // half a tier set could charge one field and silently skip another. The bag side is
  // inert without a purchasable bag product. With neither, the whole module idles and
  // every product keeps the previous behaviour.
  if (!CONFIG || (!CONFIG.enabled && !CONFIG.gavepose)) return;

  const REF_PROP = '_byRavn_gravering_ref';
  const FLAG_PROP = 'Gavepose';
  // Add-on lines are recognised by VARIANT ID, never by `item.handle`: /cart.js
  // reports the LOCALIZED handle (the Swedish storefront returns
  // `inslagning-i-presentpase` for the bag), so handle comparisons silently miss on
  // any locale with a translated handle. The ids come from the shared config, which
  // resolves the products by their canonical handles server-side.
  const BAG_VARIANT_ID = (CONFIG.gavepose && CONFIG.gavepose.variantId) || 0;
  const TIER_BY_VARIANT = {};
  Object.keys(CONFIG.tiers).forEach((tier) => {
    if (CONFIG.tiers[tier].variantId) TIER_BY_VARIANT[CONFIG.tiers[tier].variantId] = tier;
  });

  function fetchCart() {
    return fetch(`${window.routes.cart_url}.js`).then((response) => response.json());
  }

  // Shopify's AJAX cart answers errors with a JSON body carrying `status` (422 etc.);
  // a successful cart or line item never has that field.
  function postCart(url, payload) {
    return fetch(url, { ...window.fetchConfig(), body: JSON.stringify(payload) })
      .then((response) => response.json())
      .then((body) => {
        if (body && (body.status || body.errors)) throw new Error(body.description || body.message || 'cart error');
        return body;
      });
  }

  function nonce() {
    const bytes = new Uint32Array(2);
    crypto.getRandomValues(bytes);
    return 'g' + bytes[0].toString(36) + bytes[1].toString(36);
  }

  // zepto-label-suffix.liquid owns the shapes Zepto stores a label suffix in (paren,
  // bracket, bare, embedded CRLF) — rendered from theme.liquid so it exists on cart
  // pages too. Without it, recognise nothing rather than half of the shapes.
  function splitLabel(raw) {
    const splitter = window.byRavnZeptoSplitLabel;
    return splitter ? splitter(raw) : { base: String(raw == null ? '' : raw), suffix: '' };
  }

  // "(+60 kr.)" / "[+60 kr]" / "(+ 60 kr.)" → '60' when 60 is a known tier, else null.
  // The suffix text is byRavn's Danish app config, so only kr-shaped currency tokens
  // occur — localized mirror names never reach this (manifest-excluded).
  // The currency-token set here is the superset the DISPLAY strips must match
  // (stripTierSuffixIfThemeCharged in custom-symbol-keyboard.liquid, the
  // hide_price_suffix blocks in cart-line-properties.liquid): a token this charges
  // on but a strip misses would state the fee twice.
  function tierOfSuffix(suffix) {
    if (!suffix) return null;
    const amount = suffix
      .replace(/[()[\]+]/g, '')
      .replace(/kr\.?|dkk|€/gi, '')
      .trim();
    return Object.prototype.hasOwnProperty.call(CONFIG.tiers, amount) ? amount : null;
  }

  // 'gravering' | 'gavepose' | null. Nesting is what makes a line a child — the flat
  // bag line shares the gavepose VARIANT but has no parent, and must classify null so
  // the flat-bag maintenance in planActions finds it.
  function childKind(item) {
    if (!item.parent_relationship || !item.parent_relationship.parent_key) return null;
    if (item.id === BAG_VARIANT_ID) return 'gavepose';
    if (Object.prototype.hasOwnProperty.call(TIER_BY_VARIANT, item.id)) return 'gravering';
    return null;
  }

  function isChild(item) {
    return childKind(item) !== null;
  }

  // `_pplr_cart_variant` IS Zepto's charge (the cart transform keys on it). Zepto's
  // hide-in-checkout rewrite prefixes one more `_`, so match the de-underscored name.
  function isArmed(item) {
    return Object.keys(item.properties || {}).some((name) => name.replace(/^_+/, '') === 'pplr_cart_variant');
  }

  // tier → priced-field count for one line. Counts each filled property whose NAME or
  // VALUE carries a known tier suffix; name wins so a priced dropdown option inside a
  // suffixed field never counts twice.
  function pricedFields(item) {
    const properties = item.properties || {};
    const mirrorNames = new Set();
    ['_byRavn_checkout_mirrors', '__byRavn_checkout_mirrors'].forEach((key) => {
      String(properties[key] || '')
        .split('|')
        .forEach((name) => {
          if (name) {
            mirrorNames.add(name);
          }
        });
    });
    const counts = {};
    Object.keys(properties).forEach((name) => {
      const value = properties[name];
      if (value == null || !String(value).trim()) return;
      // EXACT name only. On the Danish market the raw hidden field is the mirror's name
      // plus one underscore ('_X (+60 kr.)' vs mirror 'X (+60 kr.)'), so a
      // strip-one-underscore fallback here would exclude the raw field as well and
      // count ZERO priced fields on every Danish line — free engraving. The
      // underscore-shifted manifest state only exists on armed lines (Zepto's
      // hide-in-checkout rewrite), and armed lines never reach this function.
      if (mirrorNames.has(name)) return;
      const plainName = name.replace(/^_+/, '');
      if (/^(byravn|pplr|zepto|engraving_font)/i.test(plainName)) return;
      // Name first, so a priced option inside a suffixed field never counts twice. The
      // VALUE path exists for toggles whose fee rides the chosen option ("Ønsker du
      // gravering i krydset?" → "Med gravering (+60 kr)") and is gated twice:
      // "gravering" must appear in the field NAME or in the option value's BASE (the
      // text before the suffix), and the line must carry no font capture for the
      // field. The name alone is not enough — SIA CIRCLE's toggle is named "Ønsker du
      // tekst på forsiden", so a name-only gate silently skipped its fee — but every
      // toggle's charging option reads "Med gravering (+NN kr)", which the base check
      // catches. The keyboard writes `_engraving_font:<field name>` alongside every
      // free-text engraving field (plain Arial included) and never for a toggle, so a
      // font capture proves the value is customer-typed — and text that happens to
      // end in a "+60 kr." shape must never become a charge.
      let tier = tierOfSuffix(splitLabel(plainName).suffix);
      if (!tier) {
        const hasFontCapture =
          Object.prototype.hasOwnProperty.call(properties, '_engraving_font:' + name) ||
          Object.prototype.hasOwnProperty.call(properties, '__engraving_font:' + name);
        if (!hasFontCapture) {
          const valueParts = splitLabel(String(value));
          if (/gravering/i.test(plainName) || /gravering/i.test(valueParts.base)) {
            tier = tierOfSuffix(valueParts.suffix);
          }
        }
      }
      if (tier) counts[tier] = (counts[tier] || 0) + 1;
    });
    return counts;
  }

  // want[parentKey][tier] = priced fields × parent quantity. Armed parents want NOTHING —
  // Zepto is charging them, and a child there would double-charge.
  function wantState(cart) {
    const want = {};
    (cart.items || []).forEach((item) => {
      if (isChild(item)) return;
      if (isArmed(item)) return;
      const counts = pricedFields(item);
      const tiers = Object.keys(counts);
      if (!tiers.length) return;
      want[item.key] = {};
      tiers.forEach((tier) => {
        want[item.key][tier] = counts[tier] * item.quantity;
      });
    });
    return want;
  }

  // The mutations one pass needs to bring the cart to its want-state. A child whose
  // parent_key no longer resolves is left alone — deleting on a lookup miss would turn
  // any API-shape surprise into silently freed engraving; the Liquid surfaces render an
  // unmatched child as a normal visible line instead (fail visible).
  //
  // Per-kind rules: a gravering child's target derives from the parent's tier suffixes
  // (and is skipped entirely while the fee side is disabled — zeroing children the
  // config can no longer explain would free real charges); a bag child's target is
  // simply its parent's quantity, and its EXISTENCE is never planned here — the toggle
  // owns that.
  function planActions(cart) {
    const bagVariant = CONFIG.gavepose && CONFIG.gavepose.variantId;
    const want = CONFIG.enabled ? wantState(cart) : {};
    const parents = {};
    (cart.items || []).forEach((item) => {
      parents[item.key] = item;
    });
    const changes = [];
    const additions = [];
    const seen = {};
    (cart.items || []).forEach((item) => {
      const kind = childKind(item);
      if (!kind) return;
      const parentKey = item.parent_relationship.parent_key;
      if (!parents[parentKey]) return;
      if (kind === 'gravering' && !CONFIG.enabled) return;
      const target =
        kind === 'gavepose'
          ? parents[parentKey].quantity
          : (want[parentKey] && want[parentKey][TIER_BY_VARIANT[item.id]]) || 0;
      const slot = parentKey + '|' + (kind === 'gavepose' ? 'bag' : TIER_BY_VARIANT[item.id]);
      if (seen[slot]) {
        // A duplicate child in the same slot (two half-finished passes racing) — one
        // line already carries the target quantity, this one goes.
        changes.push({ id: item.key, quantity: 0 });
        return;
      }
      seen[slot] = true;
      if (item.quantity !== target) changes.push({ id: item.key, quantity: target });
    });
    Object.keys(want).forEach((parentKey) => {
      Object.keys(want[parentKey]).forEach((tier) => {
        if (seen[parentKey + '|' + tier]) return;
        additions.push({
          id: CONFIG.tiers[tier].variantId,
          quantity: want[parentKey][tier],
          parent_line_key: parentKey,
          properties: { [REF_PROP]: nonce() },
        });
      });
    });
    // Flat-bag maintenance — see the header. Skipped when the bag product is
    // unpurchasable: a flat bag the toggle can no longer manage stays the shopper's call.
    if (bagVariant) {
      let flatBag = null;
      let flatWant = 0;
      (cart.items || []).forEach((item) => {
        if (childKind(item)) return;
        if (item.id === bagVariant) {
          flatBag = item;
          return;
        }
        if (item.properties && item.properties[FLAG_PROP]) flatWant += item.quantity;
      });
      // The flat bag is derived state, unconditionally: quantity = the flagged units,
      // zero included. This is exactly the old syncBagLine contract — want 0 with a bag
      // present removed the bag, on every toggle and cartUpdate — and dropping the
      // "leave strays alone" idea keeps toggle-off of the LAST flag from stranding a
      // paid bag line.
      if (!flatBag && flatWant > 0) {
        additions.push({ id: bagVariant, quantity: flatWant });
      } else if (flatBag && flatBag.quantity !== flatWant) {
        changes.push({ id: flatBag.key, quantity: flatWant });
      }
    }
    return { changes, additions };
  }

  async function applyActions(actions) {
    // Sequential: /cart mutations serialise server-side anyway, and a change response
    // reflects the whole cart, so firing them in parallel only invites 409-style races.
    // Returns the last response — the freshest cart evidence, for the empty-state stamp.
    let latest = null;
    for (const change of actions.changes) {
      latest = await postCart(window.routes.cart_change_url, change);
    }
    if (actions.additions.length) {
      latest = await postCart(window.routes.cart_add_url, { items: actions.additions });
    }
    return latest;
  }

  // Dawn stamps `is-empty` only inside CartItems.updateQuantity (cart.js), and
  // paintAllSurfaces swaps inner content under outer elements whose classes survive. A
  // reconcile pass is the one byRavn mutation that can EMPTY the cart — zeroing the
  // flat bag after its last armed flagged line was deleted, or removing a stray bag
  // that was the only line — and without this stamp the empty-cart markup lands inside
  // the non-empty layout and the drawer sticks as a blank white panel. /cart/add
  // responses carry no item_count, but after an add the cart cannot be empty, so
  // absent means non-empty.
  function stampEmptyState(cart) {
    const empty = cart && 'item_count' in cart ? cart.item_count === 0 : false;
    document.querySelectorAll('cart-items, cart-drawer-items, cart-drawer, #main-cart-footer').forEach((el) => {
      el.classList.toggle('is-empty', empty);
    });
  }

  let running = false;
  let rerun = false;

  // THE SETTLE HOLD. Between a cart mutation and the reconcile that resizes the fee
  // children, the painted stack is mid-flight: Dawn repaints the parent at its new
  // quantity while the child still carries the old one, so the fee row flashed a stale
  // amount (60 where 120 was due) after the spinner had already gone. While a hold is
  // active the surface roots carry `byravn-fee-settling` — CSS (byRavn-gavepose.css)
  // hides every price stack and keeps the row spinner up — and `CONFIG.settling` is a
  // promise byRavn-cart-drawer-open.js awaits before opening the drawer, so an add's
  // first visible paint is already the settled one. The class lives on <cart-items>
  // and <cart-drawer> because those elements survive every mid-settle paint — the cart
  // page swaps .js-contents inside cart-items, but drawer renders replace .drawer__inner
  // WITH the cart-drawer-items element in it, so only the outer <cart-drawer> persists.
  // The failsafe timer covers the one path that never reconciles: Dawn's quantity fetch
  // failing, whose catch publishes no cartUpdate.
  let holdTimer = null;
  let settleResolve = null;
  function holdSettle() {
    clearTimeout(holdTimer);
    holdTimer = setTimeout(() => releaseSettle(), 6000);
    document.querySelectorAll('cart-items, cart-drawer').forEach((el) => {
      el.classList.add('byravn-fee-settling');
    });
    if (!CONFIG.settling) {
      CONFIG.settling = new Promise((resolve) => {
        settleResolve = resolve;
      });
    }
  }
  function releaseSettle() {
    clearTimeout(holdTimer);
    document.querySelectorAll('cart-items, cart-drawer').forEach((el) => {
      el.classList.remove('byravn-fee-settling');
    });
    if (settleResolve) settleResolve();
    settleResolve = null;
    CONFIG.settling = null;
  }

  // Converge the cart on its want-state, verify it landed (never trust the 200 — an
  // armed parent swallows a child add silently), retry once, then repaint the cart
  // surfaces if anything moved. Re-entrant calls collapse into one trailing run.
  async function reconcile(trigger) {
    if (running) {
      rerun = true;
      return;
    }
    running = true;
    const { setBusy, surfaces, fetchCartSections, paintAllSurfaces } = window.byRavnCart || {};
    let mutated = false;
    // The freshest server evidence of the cart's state, for the empty-state stamp:
    // every fetch, then every apply's last response (which postdates the fetch).
    let latestCart = null;
    // The busy lock is depth-counted and shared (gavepose holds it across its whole
    // toggle) — take it at most once per pass and release ONLY what this pass took. A
    // no-op pass must never touch it: an unmatched release would unlock the surfaces
    // under another holder.
    let locked = false;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        // Caught per attempt, so a failed apply (a parent key gone stale between the
        // fetch and the change, a transient network error) still gets the second
        // attempt against a fresh fetch instead of aborting the whole pass.
        try {
          const cart = await fetchCart();
          latestCart = cart;
          if (!cart.item_count) break;
          const actions = planActions(cart);
          if (!actions.changes.length && !actions.additions.length) break;
          if (attempt === 1) {
            console.error('[byRavn-gravering] cart drift unresolved after retry', trigger, actions);
          }
          if (setBusy && !locked) {
            setBusy(true);
            locked = true;
          }
          mutated = true;
          const applied = await applyActions(actions);
          if (applied) latestCart = applied;
        } catch (error) {
          console.error('[byRavn-gravering]', trigger, error);
        }
      }
      if (mutated && surfaces && surfaces().length) {
        const state = await fetchCartSections();
        stampEmptyState(latestCart);
        paintAllSurfaces(state, surfaces()[0], 'byRavn-gravering');
      }
    } catch (error) {
      console.error('[byRavn-gravering]', trigger, error);
    } finally {
      if (locked) setBusy(false);
      running = false;
      if (rerun) {
        rerun = false;
        reconcile(trigger + ':rerun');
      } else {
        // The trailing reconcile is the settling authority: only when no rerun is
        // queued has the cart truly converged, so a mid-settle pass never unhides
        // the stacks early. Passes without an active hold hit a no-op here.
        releaseSettle();
      }
    }
  }

  // The armed-line gavepose toggle only writes the visible flag; the flat bag it
  // implies is derived state owned here, so byRavn-gavepose.js runs a pass right after
  // the flag write instead of waiting for the next cart mutation.
  CONFIG.reconcile = reconcile;

  // Zepto's XHR add. The detail fast-path skips the fetch when the added line is armed
  // (Zepto charging) or carries no priced field — the common Danish-market case until
  // byRavn zeroes a product — but any recognised fee goes through the full reconcile.
  document.addEventListener('byRavn:zepto-add', (event) => {
    // Fee recognition only exists when the fee side is enabled; in bag-only mode a PDP
    // add never concerns this module.
    if (!CONFIG.enabled) return;
    const item = event.detail && event.detail.item;
    if (item && item.key && isArmed(item)) return;
    if (item && item.key && !Object.keys(pricedFields(item)).length) return;
    // Synchronous, so CONFIG.settling exists before the drawer bridge (same dispatch,
    // next statement) schedules its open — the drawer then waits out the reconcile
    // and its first paint already shows the settled fee.
    holdSettle();
    reconcile('zepto-add');
  });
  // Zepto's post-add /cart/change (property rewrite, cart-widget edits): a re-key
  // migrates parent_relationship by itself, but a re-personalisation can change how many
  // fields are filled.
  document.addEventListener('byRavn:zepto-change', () => reconcile('zepto-change'));

  // Safety net behind Dawn's own mutations — cartUpdate carries the SETTLED cart. Our
  // repaints publish under 'byRavn-gravering' and are skipped, which also keeps this
  // from re-entering itself.
  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (!event || event.source !== 'cart-items') return;
    reconcile('cart-update');
  });

  // A cart drifted by an earlier failure heals on the next cart view. Gated on a
  // rendered line item, not on the surface element — the drawer exists on every page,
  // and an empty cart has nothing to heal, so this must not cost every page view a
  // /cart.js fetch.
  function onReady() {
    if (document.querySelector('cart-items .cart-item, cart-drawer-items .cart-item') && !running) {
      reconcile('page-load');
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady);
  else onReady();

  // Resize the children BEFORE delegating a parent quantity change, so Dawn's own
  // repaint already shows the right totals instead of flashing stale ones until the
  // cartUpdate subscriber catches up. Every quantity path (buttons, typed values,
  // trash-at-quantity-1) funnels through CartItems.updateQuantity, and this is its only
  // wrapper: the nested children and the flat bag resize, then Dawn. Quantity 0 is left
  // to Shopify: removing the parent cascades to its children natively, and a client-side
  // pre-remove would shift line indices under updateQuantity's positional fallback.
  //
  // SHRINK ONLY. This pre-pass sizes from the REQUESTED quantity and commits before the
  // parent moves, so between the two requests the server cart holds the new child count
  // against the old parent count. Shrinking there is an under-charge — safe, and healed
  // by the next reconcile. Growing would be an over-charge, and if the parent change
  // never lands (checkout navigation aborts Dawn's fetch, whose catch publishes no
  // cartUpdate) the oversized fee rides into the order. Increases are therefore left to
  // the cartUpdate reconcile after Dawn's change settles; the only cost is the stack
  // showing the old, lower fee for one round trip.
  function resizeChildren(live, parentKey, quantity) {
    const parent = (live.items || []).find((item) => item.key === parentKey);
    const target = parseInt(quantity, 10);
    if (!parent || isChild(parent) || !(target > 0)) return Promise.resolve(null);
    // An armed parent's fee children are the reconciler's business (it zeroes them —
    // double-charge), and pricedFields must NEVER run on an armed line (its manifest
    // state is underscore-shifted, see that function). Bag children are legitimate
    // under armed parents and resize normally.
    const feeChildrenApply = CONFIG.enabled && !isArmed(parent);
    const counts = feeChildrenApply ? pricedFields(parent) : {};
    const work = (live.items || [])
      .filter((item) => isChild(item) && item.parent_relationship.parent_key === parentKey)
      .map((item) => {
        const kind = childKind(item);
        if (kind === 'gravering' && !feeChildrenApply) return null;
        const wantQty = kind === 'gavepose' ? target : (counts[TIER_BY_VARIANT[item.id]] || 0) * target;
        if (wantQty === 0 || wantQty >= item.quantity) return null;
        return { id: item.key, quantity: wantQty };
      })
      .filter(Boolean);
    // The flat bag shrinks here too, for a flagged parent: its projected want is the
    // flagged units (planActions' derivation) with this parent at its REQUESTED
    // quantity. Without this, every armed quantity change flashed the bag as
    // an unfolded visible line for one round trip — cart-gavepose-totals sees the stale
    // bag quantity as unbalanced until the cartUpdate reconcile catches up. Shrink
    // only, same money rule as the children, and never to zero: removing a line before
    // Dawn runs would shift the line indices under updateQuantity's positional
    // stale-key fallback (target > 0 keeps the projection positive anyway). Growing is
    // left to the reconcile. Skipped when the bag product is unpurchasable, like every
    // flat-bag decision (the reconciler no longer manages that bag).
    const bagVariant = CONFIG.gavepose && CONFIG.gavepose.variantId;
    if (bagVariant && parent.properties && parent.properties[FLAG_PROP]) {
      let flatBag = null;
      let flatWant = 0;
      (live.items || []).forEach((item) => {
        if (childKind(item)) return;
        if (item.id === bagVariant) {
          flatBag = item;
          return;
        }
        if (item.properties && item.properties[FLAG_PROP]) flatWant += item.quantity;
      });
      flatWant = flatWant - parent.quantity + target;
      if (flatBag && flatWant > 0 && flatWant < flatBag.quantity) {
        work.push({ id: flatBag.key, quantity: flatWant });
      }
    }
    return work.reduce((chain, change) => chain.then(() => postCart(window.routes.cart_change_url, change)), Promise.resolve());
  }

  customElements.whenDefined('cart-items').then(() => {
    const proto = customElements.get('cart-items').prototype;
    const downstreamUpdateQuantity = proto.updateQuantity;
    proto.updateQuantity = function (line, quantity, event, name, variantId, key, isRetry) {
      // The Liquid surfaces stamp data-addon-parent on each hidden child row (fee AND
      // bag children), so a line without children skips the pre-pass fetch entirely —
      // except an ARMED wrapped line, whose bag is the flat line (no child row): its
      // checked gavepose checkbox, scoped to this surface, is the marker. A key gone
      // stale since render just misses here — the cartUpdate subscriber reconciles
      // right after.
      const hasAddonRow =
        key &&
        (this.querySelector(`[data-addon-parent="${CSS.escape(key)}"]`) ||
          this.querySelector(`.cart-gavepose__checkbox[data-line-key="${CSS.escape(key)}"]:checked`));
      if (isRetry || !hasAddonRow) return downstreamUpdateQuantity.apply(this, arguments);
      const self = this;
      const args = arguments;
      // Held from the click until the post-change reconcile settles — Dawn's own
      // repaint lands with the parent resized but the child not yet, and without the
      // hold that stale stack is visible until the cartUpdate reconcile repaints.
      holdSettle();
      self.enableLoading(line);
      fetchCart()
        .then((live) => resizeChildren(live, key, quantity))
        .catch(() => null)
        .then(() => {
          downstreamUpdateQuantity.apply(self, args);
        });
    };
  });
})();
